// "Your level was not approved" (spec 2026-11-02 §4.2): when an admin
// DISAPPROVES a published level, its creator is told why — once, the next
// time they open the game signed in. A DOM popup on the dev popups' styling;
// OK acknowledges it on the server so it never shows again.
import './devPopup.css';
import type { SupabaseClient } from '@supabase/supabase-js';
import { onIdentityChange, signedInUserId } from '../account/accountUi';
import { ackLevelNotice, myLevelNotices, type LevelNotice } from '../account/explore';

/** The popup's words (pure, so it unit-tests). */
export function levelNoticeText(n: Pick<LevelNotice, 'name' | 'reason'>): string {
  const why = n.reason.trim();
  return `Your level "${n.name}" was not approved for Explore.${why ? `\n\nReason: ${why}` : ''}`;
}

function show(n: LevelNotice, onOk: () => void): void {
  const scrim = document.createElement('div');
  scrim.className = 'dev-popup-scrim';
  // Phaser listens on the window: swallow everything at the scrim.
  for (const type of [
    'pointerdown',
    'pointerup',
    'mousedown',
    'mouseup',
    'click',
    'touchstart',
    'touchend',
  ]) {
    scrim.addEventListener(type, (e) => e.stopPropagation());
  }
  const panel = document.createElement('div');
  panel.className = 'dev-popup dev-popup-danger';
  panel.dataset.testid = 'level-notice';
  const text = document.createElement('div');
  text.className = 'dev-popup-text';
  text.dataset.testid = 'level-notice-text';
  text.style.whiteSpace = 'pre-line';
  text.textContent = levelNoticeText(n);
  const ok = document.createElement('button');
  ok.className = 'dev-popup-ok';
  ok.dataset.testid = 'level-notice-ok';
  ok.textContent = 'OK';
  ok.addEventListener('click', () => {
    scrim.remove();
    onOk();
  });
  panel.append(text, ok);
  scrim.append(panel);
  // In fullscreen only the fullscreen element is drawn (a body overlay is not).
  (document.fullscreenElement ?? document.body).append(scrim);
}

/** Check once per signed-in account per page load; show them one at a time. */
export function initLevelNotices(client: SupabaseClient): void {
  const checked = new Set<string>();
  const check = async () => {
    const uid = signedInUserId();
    if (!uid || checked.has(uid)) return;
    checked.add(uid);
    const queue = await myLevelNotices(client);
    const next = () => {
      const n = queue.shift();
      if (!n) return;
      show(n, () => {
        void ackLevelNotice(client, n.sourceId);
        next();
      });
    };
    next();
  };
  onIdentityChange(() => void check());
  void check();
}
